import { useLocation } from 'react-router-dom'
import { ColorLens } from '@mui/icons-material'
import { Tabs, Tab, Typography } from '@mui/material'
import { ToggleButton, ToggleButtonGroup } from '@ui/actions'
import { DraggablePopup } from '@ui/structure'
import { useControlStore } from '@editorStores'
import ColoringAssemblies from '../ColoringAssemblies'
import ColoringGroupedElements from '../ColoringGroupedElements'
import ColoringProblems from '../ColoringProblems'
import ColoringUtilization from '../ColoringUtilization'

interface Props {
  onSelectElements: (elements: string[]) => void
}

const ColoringControls = ({ onSelectElements }: Props) => {
  const { pathname } = useLocation()
  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const setProblemViewActiveErrors = useControlStore(state => state.setProblemViewActiveErrors)

  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)
  const setProblemViewActiveWarnings = useControlStore(state => state.setProblemViewActiveWarnings)

  const coloringMode = useControlStore(state => state.coloringMode)
  const setColoringMode = useControlStore(state => state.setColoringMode)

  const coloringAnchorEl = useControlStore(state => state.coloringAnchorEl)
  const setColoringAnchorEl = useControlStore(state => state.setColoringAnchorEl)

  const handleTabChange = (event: React.SyntheticEvent, newValue: ColoringMode) => {
    setColoringMode(newValue)
  }

  return (
    <>
      <ToggleButtonGroup>
        <ToggleButton
          hint="Einfärbung des Modells"
          onClick={event => setColoringAnchorEl(event.currentTarget)}
          selected={false}
          Icon={ColorLens}
        />
      </ToggleButtonGroup>

      <DraggablePopup
        anchorEl={coloringAnchorEl || undefined}
        closePopover={() => setColoringAnchorEl(null)}
        title="Einfärbung des Modells"
        childrenBoxSx={{
          minWidth: coloringMode === 'coloringAssemblies' ? 900 : 600,
        }}
      >
        <Tabs
          value={coloringMode}
          onChange={handleTabChange}
          sx={{ mb: 1, paddingX: 0, paddingY: 0 }}
        >
          <Tab label="Probleme/Warnungen" value="problemView" sx={{ paddingX: 1, paddingY: 0 }} />
          <Tab label="Aufbauten" value="coloringAssemblies" sx={{ paddingX: 1, paddingY: 0 }} />
          <Tab label="Stockwerk" value="storey" sx={{ paddingX: 1, paddingY: 0 }} />
          <Tab
            label="Gruppierte Elemente"
            value="groupedElements"
            sx={{ paddingX: 1, paddingY: 0 }}
            disabled={!pathname.includes('results')}
          />
          <Tab
            label="Auslastung η"
            value="utilization"
            sx={{ paddingX: 1, paddingY: 0 }}
            disabled={!pathname.includes('results')}
          />
          <Tab
            label="Auslastung ηc,90"
            value="utilization90"
            sx={{ paddingX: 1, paddingY: 0 }}
            disabled={!pathname.includes('results')}
          />
        </Tabs>

        {coloringMode === 'problemView' && (
          <ColoringProblems
            isActiveErrors={problemViewActiveErrors}
            setIsActiveErrors={setProblemViewActiveErrors}
            isActiveWarnings={problemViewActiveWarnings}
            setIsActiveWarnings={setProblemViewActiveWarnings}
          />
        )}

        {coloringMode === 'coloringAssemblies' && (
          <ColoringAssemblies onSelectElements={onSelectElements} />
        )}

        {coloringMode === 'storey' && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Die Einfärbung wird pro Stockwerk in der Stockwerkssteuerung festgelegt.
          </Typography>
        )}

        {coloringMode === 'groupedElements' && <ColoringGroupedElements />}

        {(coloringMode === 'utilization' || coloringMode === 'utilization90') && (
          <ColoringUtilization />
        )}
      </DraggablePopup>
    </>
  )
}

export default ColoringControls
